import React, { useEffect, useRef, useState, useCallback } from "react";
import { apiKycOptions } from "@/lib/api";

type Step = 1 | 2 | 3 | 4;
type QualityReport = { sharpness: number; brightness: number; glarePct: number };
type DocumentSideMode = "front_only" | "front_back";
type CountryOption = [string, string];

const fallbackDocumentCountries: CountryOption[] = [
  ["EE", "Estonia"],
  ["SE", "Sweden"],
  ["FI", "Finland"],
  ["NO", "Norway"],
  ["DK", "Denmark"],
  ["GB", "United Kingdom"],
  ["IE", "Ireland"],
  ["DE", "Germany"],
  ["FR", "France"],
  ["ES", "Spain"],
  ["IT", "Italy"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["PL", "Poland"],
  ["LT", "Lithuania"],
  ["LV", "Latvia"],
  ["US", "United States"],
  ["CA", "Canada"],
  ["AU", "Australia"],
  ["NZ", "New Zealand"],
  ["ZZ", "Other / not listed"],
];

const documentTypeRules: Record<string, { label: string; defaultSides: DocumentSideMode }> = {
  passport: { label: "Passport", defaultSides: "front_only" },
  national_id: { label: "National ID", defaultSides: "front_back" },
  driving_license: { label: "Driving License", defaultSides: "front_back" },
};

const documentCountryOverrides: Record<string, Record<string, DocumentSideMode>> = {
  EE: { national_id: "front_back" },
};

function documentSideMode(type: string, country: string): DocumentSideMode {
  return documentCountryOverrides[country]?.[type] || documentTypeRules[type]?.defaultSides || "front_only";
}

function documentRequiresBack(type: string, country: string): boolean {
  return documentSideMode(type, country) === "front_back";
}

const incomeOptions = [
  ["salary", "Salary / employment"],
  ["business_income", "Business income"],
  ["investment_income", "Investment income"],
  ["savings", "Savings"],
  ["pension", "Pension"],
  ["inheritance", "Inheritance"],
  ["property_sale", "Property sale"],
  ["other", "Other"],
];

const SHARPNESS_MIN = 90;
const BRIGHTNESS_MIN = 70;
const BRIGHTNESS_MAX = 220;
const GLARE_MAX_PCT = 7;

/** Read CSRF token from cookie for Sanctum/VerifyCsrfToken */
function getXsrfToken(): string {
  const m = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

/** Client-side mirror of your "expiry ≥ 6 months" rule */
function parseIsoDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  d.setHours(0, 0, 0, 0);

  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }

  return d;
}

function isExpiryOk(value: string): boolean {
  const d = parseIsoDate(value);
  if (!d) return false;

  const now = new Date();
  const min = new Date(now);
  min.setMonth(min.getMonth() + 6);
  min.setHours(0, 0, 0, 0);
  return d.getTime() >= min.getTime();
}

export default function KycCreate() {
  const queryParams = new URLSearchParams(window.location.search);
  const initialDocType = queryParams.get("document_type") || "";
  const initialDocCountry = (queryParams.get("document_country") || "").toUpperCase();

  const [step, setStep] = useState<Step>(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [documentCountries, setDocumentCountries] = useState<CountryOption[]>(fallbackDocumentCountries);

  // required by your controller
  const [docType, setDocType] = useState(() =>
    ["passport", "national_id", "driving_license"].includes(initialDocType)
      ? initialDocType
      : "passport"
  );
  const [docCountry, setDocCountry] = useState(() =>
    /^[A-Z]{2}$/.test(initialDocCountry) ? initialDocCountry : ""
  );
  const [docNumber, setDocNumber] = useState(() => queryParams.get("document_number") || "");
  const [docExpiry, setDocExpiry] = useState(() => queryParams.get("document_expiry") || "");
  const [handoffUboId] = useState(() => queryParams.get("ubo_id") || "");

  // captured blobs
  const [docBlob, setDocBlob] = useState<Blob | null>(null);
  const [docBackBlob, setDocBackBlob] = useState<Blob | null>(null);
  const [selfieBlob, setSelfieBlob] = useState<Blob | null>(null);

  // PoA file upload (step 3)
  const [poaFile, setPoaFile] = useState<File | null>(null);
  const [isPep, setIsPep] = useState("");
  const [pepDetails, setPepDetails] = useState("");
  const [sourceOfIncome, setSourceOfIncome] = useState("");
  const [sourceOther, setSourceOther] = useState("");

  const needsDocumentBack = documentRequiresBack(docType, docCountry);

  useEffect(() => {
    let mounted = true;

    apiKycOptions()
      .then((options) => {
        if (!mounted || !Array.isArray(options.document_countries)) return;

        const countries = options.document_countries
          .filter((country) => country?.code && country?.label)
          .map((country) => [country.code, country.label] as CountryOption);

        if (countries.length > 0) {
          setDocumentCountries(countries);
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!needsDocumentBack) setDocBackBlob(null);
  }, [needsDocumentBack]);

  const canContinue =
    (step === 1 && !!docBlob && (!needsDocumentBack || !!docBackBlob) && !!docType && !!docCountry && !!docExpiry && !!docNumber) ||
    (step === 2 && !!selfieBlob) ||
    (step === 3 &&
      !!poaFile &&
      !!isPep &&
      (isPep !== "1" || !!pepDetails.trim()) &&
      !!sourceOfIncome &&
      (sourceOfIncome !== "other" || !!sourceOther.trim()));

  function goBack() {
    if (step > 1 && step < 4) setStep((s) => (s - 1) as Step);
  }

  function goNext() {
    if (step === 1) {
      if (!docCountry) return setError("Please select the issuing country for your ID.");
      if (!docNumber.trim()) return setError("Please enter your document number.");
      if (!docExpiry) return setError("Please select your document expiry date.");
      if (!isExpiryOk(docExpiry)) {
        return setError("Document expiry must be at least 6 months from today.");
      }
      if (!docBlob) return setError("Please take a photo of your document.");
      if (needsDocumentBack && !docBackBlob) {
        return setError("Please take a photo of the back side of your document.");
      }
      setError(null);
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!selfieBlob) {
        return setError("Please take your selfie before continuing.");
      }
      setError(null);
      setStep(3);
      return;
    }

    if (step === 3) {
      if (!poaFile) {
        return setError("Please upload a proof of address document.");
      }
      if (!isPep) return setError("Please answer the PEP question.");
      if (isPep === "1" && !pepDetails.trim()) return setError("Please add PEP details.");
      if (!sourceOfIncome) return setError("Please select your source of income.");
      if (sourceOfIncome === "other" && !sourceOther.trim()) {
        return setError("Please describe your source of income.");
      }
      setError(null);
      handleSubmit();
      return;
    }
  }

  async function handleSubmit() {
    if (!docBlob || (needsDocumentBack && !docBackBlob) || !selfieBlob || !poaFile) {
      setError("Missing document, selfie, or proof of address.");
      return;
    }
    setBusy(true);
    setError(null);

    try {
      try {
        await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      } catch {}

      const form = new FormData();
      form.append("document_type", docType);
      form.append("document_country", docCountry);
      form.append("document_number", docNumber);
      form.append("document_expiry", docExpiry);
      form.append("id_document_front", docBlob, "id_document_front.jpg");
      if (docBackBlob) form.append("id_document_back", docBackBlob, "id_document_back.jpg");
      form.append("selfie_photo", selfieBlob, "selfie_photo.jpg");
      form.append("poa_file", poaFile, poaFile.name || "poa_document.jpg");
      form.append("is_pep", isPep);
      form.append("pep_details", pepDetails);
      form.append("source_of_income", sourceOfIncome);
      form.append("source_of_income_other", sourceOther);
      if (handoffUboId) form.append("ubo_id", handoffUboId);

      const resp = await fetch("/xapi/kyc", {
        method: "POST",
        body: form,
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "X-XSRF-TOKEN": getXsrfToken(),
          Accept: "application/json",
        },
      });

      if (resp.ok) {
        setStep(4);
        return;
      }

      if (resp.status === 422) {
        let message = "Validation failed.";
        try {
          const data = await resp.json();
          if (data?.errors && typeof data.errors === "object") {
            const first = Object.values<any>(data.errors)[0];
            if (Array.isArray(first) && first[0]) message = first[0];
          } else if (data?.message) {
            message = data.message;
          }
        } catch {}
        setError(message);
        return;
      }

      setError(`HTTP ${resp.status}`);
    } catch (e: any) {
      setError(e?.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto px-4 pb-28 pt-4">
      <div className="section-box p-4 md:p-6">
        <h1 className="text-2xl font-bold">Identity Verification</h1>
        {step <= 3 && (
          <p className="text-gray-500 mt-1 mb-4">Step {step} of 3</p>
        )}
        {error && <div className="alert-danger mb-4">{error}</div>}

        {step === 1 && (
          <>
            <div className="form-section">
              <h2 className="text-lg font-semibold mb-3">Document Details</h2>

              <div className="grid gap-4">
                <label className="block">
                  <span className="block text-sm font-medium mb-1">Document Type</span>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                    <option value="driving_license">Driving License</option>
                  </select>
                </label>

                <label className="block">
                  <span className="block text-sm font-medium mb-1">ID Country</span>
                  <select
                    value={docCountry}
                    onChange={(e) => setDocCountry(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select issuing country</option>
                    {documentCountries.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="block text-sm font-medium mb-1">Document Number</span>
                  <input
                    type="text"
                    value={docNumber}
                    onChange={(e) => setDocNumber(e.target.value)}
                    placeholder="Enter your document number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </label>

                <label className="block">
                  <span className="block text-sm font-medium mb-1">Expiry Date</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="YYYY-MM-DD"
                    pattern="\\d{4}-\\d{2}-\\d{2}"
                    title="Use YYYY-MM-DD"
                    value={docExpiry}
                    onChange={(e) => setDocExpiry(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use YYYY-MM-DD. Must be at least 6 months from today.
                  </p>
                </label>
              </div>
            </div>

            <div className="form-section mt-6">
              <h3 className="text-lg font-semibold mb-1">Take Photo of Your Document</h3>
              <p className="text-gray-600 mb-3">
                {needsDocumentBack
                  ? "Capture the front side first, then the back side."
                  : "Capture the passport photo page / front side."}
              </p>
              <CaptureBlock
                rounded
                title="Document front capture"
                hint=""
                facingMode="environment"
                onCaptured={(b) => setDocBlob(b)}
                onConfirm={() => {
                  if (needsDocumentBack) {
                    setError("Front saved. Capture the back side before continuing.");
                    document.getElementById("doc-back-capture")?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    setStep(2);
                  }
                }}
              />
              {needsDocumentBack && (
                <div id="doc-back-capture" className="mt-6">
                  <h4 className="text-base font-semibold mb-1">Document Back</h4>
                  <p className="text-gray-600 mb-3">Turn the card over and capture the back side.</p>
                  <CaptureBlock
                    rounded
                    title="Document back capture"
                    hint=""
                    facingMode="environment"
                    onCaptured={(b) => setDocBackBlob(b)}
                    onConfirm={() => setStep(2)}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {step === 2 && (
          <div className="form-section">
            <h3 className="text-lg font-semibold mb-1">Take a Selfie</h3>
            <p className="text-gray-600 mb-3">Center your face, remove glasses or hat</p>
            <CaptureBlock
              rounded
              title="Selfie capture"
              hint=""
              facingMode="user"
              onCaptured={(b) => setSelfieBlob(b)}
              onConfirm={() => setStep(3)}
            />
          </div>
        )}

        {step === 3 && (
          <div className="form-section">
            <h3 className="text-lg font-semibold mb-1">Upload Proof of Address</h3>
            <p className="text-gray-600 mb-3">
              Upload a recent utility bill, bank statement, or government letter showing your
              name and address (usually not older than 3 months).
            </p>
            <div className="grid gap-4">
              <label className="block">
                <span className="block text-sm font-medium mb-1">Proof of Address file</span>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => setPoaFile(e.target.files?.[0] || null)}
                  className="w-full text-sm"
                />
              </label>
              {poaFile && (
                <p className="text-xs text-gray-500">
                  Selected: <span className="font-medium">{poaFile.name}</span>
                </p>
              )}
              <label className="block">
                <span className="block text-sm font-medium mb-1">
                  Are you a politically exposed person (PEP)?
                </span>
                <select
                  value={isPep}
                  onChange={(e) => setIsPep(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select...</option>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </label>
              {isPep === "1" && (
                <label className="block">
                  <span className="block text-sm font-medium mb-1">PEP details</span>
                  <textarea
                    value={pepDetails}
                    onChange={(e) => setPepDetails(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    maxLength={1000}
                    required
                  />
                </label>
              )}
              <label className="block">
                <span className="block text-sm font-medium mb-1">Primary source of income</span>
                <select
                  value={sourceOfIncome}
                  onChange={(e) => setSourceOfIncome(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select...</option>
                  {incomeOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {sourceOfIncome === "other" && (
                <label className="block">
                  <span className="block text-sm font-medium mb-1">Other source</span>
                  <input
                    type="text"
                    value={sourceOther}
                    onChange={(e) => setSourceOther(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={255}
                    required
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-16">
            <div className="text-4xl mb-3">✅</div>
            <h2 className="text-xl font-semibold mb-2">Submitted!</h2>
            <p className="text-gray-600">We’ll start verifying your identity shortly.</p>
          </div>
        )}

        {step < 4 && (
          <div className="mt-6 flex items-center justify-between gap-3">
            {step > 1 ? (
              <button className="btn btn-white" onClick={goBack}>
                Back
              </button>
            ) : (
              <span />
            )}
            <button
              className={`btn ${!canContinue || busy ? "opacity-60 pointer-events-none" : ""}`}
              onClick={goNext}
            >
              {busy ? "Uploading..." : step === 3 ? "Submit" : "Continue"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------
 * Capture Component
 * -----------------------*/
function CaptureBlock({
  title,
  hint,
  facingMode,
  onCaptured,
  onConfirm,
  rounded = true,
}: {
  title: string;
  hint: string;
  facingMode: "user" | "environment";
  onCaptured: (b: Blob) => void;
  onConfirm?: () => void;
  rounded?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [quality, setQuality] = useState<QualityReport | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [camKey, setCamKey] = useState(0); // forces <video> remounts

  // Hide bottom tab bar while camera is live (prevents iOS compositing glitches)
  useEffect(() => {
    if (!preview) document.body.classList.add("camera-open");
    else document.body.classList.remove("camera-open");
    return () => document.body.classList.remove("camera-open");
  }, [preview]);

  // start / rebuild camera whenever camKey or facingMode changes
  useEffect(() => {
    let stream: MediaStream | null = null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        const v = videoRef.current;
        if (v) {
          v.setAttribute("playsinline", "true");
          (v as any).setAttribute?.("webkit-playsinline", "true");
          v.muted = true;
          v.srcObject = stream;
          // Some iOS builds need a tick before play()
          requestAnimationFrame(() => {
            v.play().catch(() => {});
          });
        }
      } catch {
        setMsg("Camera access denied or unavailable.");
      }
    })();

    const onVis = () => {
      if (document.visibilityState === "visible") {
        const v = videoRef.current;
        if (v && v.paused) v.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [camKey, facingMode]);

  // Retake: fully rebuild the stream + remount <video>
  const retake = useCallback(async () => {
    setPreview(null);
    setMsg(null);

    const v = videoRef.current;
    if (v) {
      const old = v.srcObject as MediaStream | null;
      if (old) old.getTracks().forEach((t) => t.stop());
      v.srcObject = null;
    }

    // Force React to create a brand new <video> node
    setCamKey((k) => k + 1);
  }, []);

  const capture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const vw = videoRef.current.videoWidth || 1280;
    const vh = videoRef.current.videoHeight || 720;

    canvasRef.current.width = vw;
    canvasRef.current.height = vh;
    const ctx = canvasRef.current.getContext("2d")!;
    ctx.drawImage(videoRef.current, 0, 0, vw, vh);

    const imgData = ctx.getImageData(0, 0, vw, vh);
    const q = analyzeQuality(imgData);
    setQuality(q);

    if (q.sharpness < SHARPNESS_MIN) return setMsg("Image is blurry, hold steady.");
    if (q.brightness < BRIGHTNESS_MIN) return setMsg("Too dark, improve lighting.");
    if (q.brightness > BRIGHTNESS_MAX) return setMsg("Too bright / glare.");
    if (q.glarePct > GLARE_MAX_PCT) return setMsg("Glare detected on document.");

    canvasRef.current.toBlob(
      (b) => {
        if (!b) return;
        setMsg(null);
        setPreview(URL.createObjectURL(b));
        onCaptured(b);
      },
      "image/jpeg",
      0.88
    );
  }, [onCaptured]);

  return (
    <div>
      {!!title && <h4 className="sr-only">{title}</h4>}
      {!!hint && <p className="sr-only">{hint}</p>}

      <div
        className={`relative bg-black ${rounded ? "rounded-2xl" : ""} overflow-hidden`}
        style={{ aspectRatio: "3 / 4" }}
      >
        {!preview ? (
          <div className="relative w-full h-full">
            <video
              key={camKey} // <-- remount on retake
              ref={videoRef}
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-4 md:inset-6 rounded-xl border-4 border-white/80 pointer-events-none"
              style={{ boxShadow: "0 0 0 2000px rgba(0,0,0,0.45)" }}
            />
            <div
              className={`absolute top-3 left-3 right-3 ${
                msg ? "bg-red-600" : "bg-green-600"
              } text-white text-sm rounded-md px-3 py-2`}
            >
              {msg || "Ready — hold steady and tap shutter"}
            </div>
            <div className="absolute bottom-4 left-0 right-0 flex justify-center">
              <button
                className="rounded-full w-20 h-20 bg-white active:scale-95 transition"
                onClick={capture}
                aria-label="Shutter"
              />
            </div>
          </div>
        ) : (
          <img src={preview} alt="Preview" className="w-full h-full object-contain bg-black" />
        )}
      </div>

      {preview && (
        <div className="mt-3 flex gap-3">
          <button className="btn btn-white" onClick={retake}>
            Retake
          </button>
          <button className="btn" onClick={() => onConfirm?.()}>
            Looks good
          </button>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
      {quality && (
        <div className="mt-2 text-xs text-gray-500">
          Quality: sharpness {quality.sharpness.toFixed(0)}, brightness{" "}
          {quality.brightness.toFixed(0)}, glare {quality.glarePct.toFixed(1)}%
        </div>
      )}
    </div>
  );
}

/* ------------------------
 * Quality helpers (fast & simple)
 * -----------------------*/
function analyzeQuality(img: ImageData): QualityReport {
  const { data, width, height } = img;
  let sum = 0;
  let glare = 0;
  const gray = new Uint8ClampedArray(width * height);

  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const r = data[i],
      g = data[i + 1],
      b = data[i + 2];
    const v = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    gray[j] = v;
    sum += v;
    if (r > 250 && g > 250 && b > 250) glare++;
  }

  const brightness = sum / (width * height);
  const glarePct = (glare / (width * height)) * 100;
  const sharpness = varianceOfLaplacian(gray, width, height);
  return { brightness, glarePct, sharpness };
}

function varianceOfLaplacian(gray: Uint8ClampedArray, w: number, h: number) {
  let sum = 0,
    sumSq = 0,
    count = 0;
  const K = [0, 1, 0, 1, -4, 1, 0, 1, 0];
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const v =
        gray[i - w] * K[1] +
        gray[i - 1] * K[3] +
        gray[i] * K[4] +
        gray[i + 1] * K[5] +
        gray[i + w] * K[7];
      sum += v;
      sumSq += v * v;
      count++;
    }
  }
  const mean = sum / count;
  return Math.max(0, sumSq / count - mean * mean);
}
